import  { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box,  Typography, FormControl, InputLabel, Select, MenuItem, Fab, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import axios from 'axios';
import CardItem from './CardItem';
import CardDetailsDialog from './CardDetailsDialog';
import { getCardImageUrl } from './cardImageUtils';
import { addToPreloadQueue } from './imageCache';
import priceService from '../../services/PriceService';
import searchService from '../../services/SearchService';
import splinterlandsSettingsService from '../../services/SplinterlandsSettingsService';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import MarketHeader from '../../components/market/MarketHeader';
import LeaderboardDialog from '../../components/market/LeaderboardDialog';
import InfoDialog from '../../components/market/InfoDialog';

// Import filter constants instead of redefining them
import { 
  CARD_ELEMENTS, 
  CARD_BLOODLINES,
  getElementInfoByColor,
  getRarityInfoByValue,
  matchesAnyCategory
} from "../../constants/filterConstants";

const Market = ({ setIsSidebar, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  
  // Remove activeTab state since it's now handled by route
  
  const [cards, setCards] = useState([]);
  const [cardDetails, setCardDetails] = useState([]);
  const [visibleCount, setVisibleCount] = useState(60); // 5 Reihen à 12 Karten (oder mehr)
  const [, setPage] = useState(1);
  const cardsPerPage = 60;
  const [showBaseImages, setShowBaseImages] = useState(true); // Schalter für Basis-Bilder
  const [refreshImageKey, setRefreshImageKey] = useState(Date.now()); // Key zum Erzwingen einer Aktualisierung
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardDetails, setSelectedCardDetails] = useState(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const sentinelRef = useRef(null);
  const [, setCurrentDecPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchIndex, setSearchIndex] = useState({});
  const cardsRef = useRef([]); // Fix: add cardsRef for priceService subscription
  const [isInitialLoading, setIsInitialLoading] = useState(true); // Track initial data load
  const [, setIsLoading] = useState(false);
  const [, setLastRefreshed] = useState(Date.now());
  const [blockchainSettings, setBlockchainSettings] = useState(null);
  const [sampleMarketData, setSampleMarketData] = useState(null);
  const refreshIntervalRef = useRef(null);
  const eventListenerAddedRef = useRef(false);
  const reloadMarketDataRef = useRef(null);
  
  // Function to refresh market data when needed - defined early to avoid reference errors
  const reloadMarketData = useCallback(async () => {
    setIsLoading(true);
  
    try {
      // Add cache buster parameter to ensure fresh market data
      const cacheBuster = Math.random().toString(36).substring(2, 15);
      const marketRes = await axios.get(`https://api.splinterlands.com/market/for_sale_grouped?v=${cacheBuster}`);
      
      const marketData = Array.isArray(marketRes.data) ? marketRes.data : marketRes.data?.data || [];
      
      // Create a map for quick lookup of card details
      const detailsMap = new Map();
      cardDetails.forEach(detail => {
        detailsMap.set(detail.id, detail);
      });
      
      // Process the market data - API liefert bereits USD-Preise
      const enhancedMarketData = marketData.map(card => {
        const cardDetails = detailsMap.get(card.card_detail_id);
        
        // Preise direkt aus API verwenden (bereits in USD)
        const originalPrice = card.low_price;
        const originalPriceBcx = card.low_price_bcx;
        
        // Keine Konvertierung nötig - API liefert bereits USD
        let normalizedPrice = originalPrice;
        let normalizedPriceBcx = originalPriceBcx;
        
        if (normalizedPrice !== undefined && normalizedPrice !== null) {
          normalizedPrice = Number(normalizedPrice);
        }
        
        if (normalizedPriceBcx !== undefined && normalizedPriceBcx !== null) {
          normalizedPriceBcx = Number(normalizedPriceBcx);
        }
        
        if (cardDetails) {
          return {
            ...card,
            low_price: normalizedPrice,
            low_price_bcx: normalizedPriceBcx,
            card_name: cardDetails.name
          };
        }
        return {
          ...card,
          low_price: normalizedPrice,
          low_price_bcx: normalizedPriceBcx
        };
      });
      
      setCards(enhancedMarketData);
      setLastRefreshed(Date.now());

      // Take a small sample of market data to monitor for changes
      // This is used to detect if a card has been sold without reloading all cards
      if (enhancedMarketData.length > 0) {
        // Get a representative sample (first 20 cards or so)
        const sample = enhancedMarketData.slice(0, 20);
        setSampleMarketData(sample.map(card => ({
          uid: card.uid,
          card_detail_id: card.card_detail_id,
          low_price: card.low_price,
          market_id: card.market_id
        })));
      }
    } catch (error) {
      console.error('Error reloading market data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [cardDetails]);
  
  // Store reference to reloadMarketData for event handlers
  useEffect(() => {
    reloadMarketDataRef.current = reloadMarketData;
  }, [reloadMarketData]);
  
  // Use ref to track current blockchain settings to avoid dependency issues
  const blockchainSettingsRef = useRef(null);
  
  // Sync ref with state
  useEffect(() => {
    blockchainSettingsRef.current = blockchainSettings;
  }, [blockchainSettings]);
  
  // Function to refresh blockchain settings
  const refreshBlockchainSettings = useCallback(async () => {
    try {
      // Use cached settings service to avoid redundant API calls
      const settings = await splinterlandsSettingsService.getSettings();
      
      if (settings && settings.last_block) {
        // Only trigger a re-render if the block has changed
        const hasChanged = !blockchainSettingsRef.current || 
                          blockchainSettingsRef.current.last_block !== settings.last_block;
                          
        if (hasChanged) {
          blockchainSettingsRef.current = settings;
          setBlockchainSettings(settings);
          window.splinterlandsBlockchainSettings = settings;
        }
      }
    } catch (error) {
      console.error('Error refreshing blockchain settings:', error);
    }
  }, []); // Remove blockchainSettings dependency
  
  // Function to check if sample market data has changed (cards sold)
  const checkMarketChanges = useCallback(async () => {
    // Skip if no sample data yet
    if (!sampleMarketData || sampleMarketData.length === 0) return;
    
    try {
      // Build list of UIDs from sample
      const sampleUids = sampleMarketData.map(item => item.uid).join(',');
      const cacheBuster = Math.random().toString(36).substring(2, 15);
      
      // Check current status of sampled cards
      const response = await axios.get(
        `https://api2.splinterlands.com/cards/find?ids=${sampleUids}&v=${cacheBuster}`
      );
      
      if (!response.data || !Array.isArray(response.data)) return;
      
      // Check if any cards were sold (changed market_id or missing)
      let changesDetected = false;
      
      // Quick lookup map from our sample
      const sampleMap = {};
      sampleMarketData.forEach(card => {
        sampleMap[card.uid] = card;
      });
      
      // Check each card in the response
      for (const card of response.data) {
        const originalData = sampleMap[card.uid];
        if (!originalData) continue;
        
        // If market_listing_status changed or market_id changed
        if (card.market_listing_status !== 0 || 
            (card.market_id && originalData.market_id && 
             card.market_id !== originalData.market_id)) {
          changesDetected = true;
          break;
        }
      }
      
      // If we detected changes, reload market data
      if (changesDetected) {
        reloadMarketData();
      }
    } catch (error) {
      console.error('Error checking market changes:', error);
    }
  }, [sampleMarketData, reloadMarketData]);
  
  // Set up event listener for reloadMarketData events from other components
  useEffect(() => {
    // Store the reference to reloadMarketData for event listeners
    reloadMarketDataRef.current = reloadMarketData;
    
    // Only set up the listener once
    if (!eventListenerAddedRef.current) {
      const handleReloadMarketData = () => {
      
        if (reloadMarketDataRef.current) {
          reloadMarketDataRef.current();
        }
      };
      
      window.addEventListener('reloadMarketData', handleReloadMarketData);
      eventListenerAddedRef.current = true;
      
      return () => {
        window.removeEventListener('reloadMarketData', handleReloadMarketData);
        eventListenerAddedRef.current = false;
      };
    }
  }, [reloadMarketData]);
  
  // Setup periodic background updates (settings only every 60s)
  useEffect(() => {
    // Don't set up listeners multiple times
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Initial data loads
    refreshBlockchainSettings();
    
    // Set up interval for background refreshes that don't affect the UI unless necessary
    refreshIntervalRef.current = setInterval(() => {
      // Always refresh blockchain settings (lightweight)
      refreshBlockchainSettings();
      
      // Check market for changes (using sample data) every 60s
      checkMarketChanges();
    }, 60000); // 60 seconds
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [refreshBlockchainSettings, checkMarketChanges]);
  
  // Subscribe to search service for search term updates
  useEffect(() => {
    // Get any existing search term
    const initialTerm = searchService.getSearchTerm();
    if (initialTerm) {
      setSearchTerm(initialTerm);
    }
    
    // Subscribe to search term changes
    const unsubscribe = searchService.subscribeToSearchTerm(term => {
      setSearchTerm(term);
    });
    
    return () => unsubscribe();
  }, []);
  
  // Subscribe to search filters
  const [selectedElementFilters, setSelectedElementFilters] = useState([]);
  const [selectedRarities, setSelectedRarities] = useState([]);
  const [selectedFoilTypes, setSelectedFoilTypes] = useState([]);
  const [selectedSetCategories, setSelectedSetCategories] = useState([]); // New: Hierarchical set categories
  const [selectedBloodlines, setSelectedBloodlines] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]); // New: Unit vs Archon filter
  
  // Price sorting state - standardmäßig nach billigstem Preis sortieren
  const [sortOption, setSortOption] = useState('price_asc');

  useEffect(() => {
    // Get initial filters
    const initialFilters = searchService.getFilters();
    if (initialFilters.elements) {
      setSelectedElementFilters(initialFilters.elements);
    }
    if (initialFilters.rarities) {
      setSelectedRarities(initialFilters.rarities);
    }
    if (initialFilters.foilTypes) {
      setSelectedFoilTypes(initialFilters.foilTypes);
    }
    if (initialFilters.setCategories) {
      setSelectedSetCategories(initialFilters.setCategories);
    }
    if (initialFilters.bloodlines) {
      setSelectedBloodlines(initialFilters.bloodlines);
    }
    if (initialFilters.types) {
      setSelectedTypes(initialFilters.types);
    }
    // Subscribe to filter changes
    const unsubscribe = searchService.subscribeToFilters(filters => {
      if (filters.elements) {
        setSelectedElementFilters(filters.elements);
      }
      if (filters.rarities) {
        setSelectedRarities(filters.rarities);
      }
      if (filters.foilTypes) {
        setSelectedFoilTypes(filters.foilTypes);
      }
      if (filters.setCategories) {
        setSelectedSetCategories(filters.setCategories);
      }
      if (filters.bloodlines) {
        setSelectedBloodlines(filters.bloodlines);
      }
      if (filters.types) {
        setSelectedTypes(filters.types);
      }
    });
    return () => unsubscribe();
  }, []);
  
  // Keep cardsRef in sync with cards state
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);
  
  useEffect(() => {
    // Get initial price
    const { prices } = priceService.getData();
    if (prices && prices.dec) {
      setCurrentDecPrice(prices.dec);
    }

    // Subscribe to price service updates
    const unsubscribe = priceService.subscribe(({ prices }) => {
      if (prices && prices.dec) {
        setCurrentDecPrice(prices.dec);
        // Note: No need to update card prices anymore since API provides USD directly
      }
    });
    return () => unsubscribe();
  }, []); // Fix: remove cards.length from deps, only run on mount
  
  // Listen for toggleBaseImages events from sidebar
  useEffect(() => {
    const handleToggleBaseImages = (event) => {
      setShowBaseImages(event.detail.showBaseImages);
      setRefreshImageKey(Date.now()); // Force refresh of images
    };
    
    window.addEventListener('toggleBaseImages', handleToggleBaseImages);
    
    return () => {
      window.removeEventListener('toggleBaseImages', handleToggleBaseImages);
    };
  }, []);
  
  // Handle card click to open details dialog
  const handleCardClick = (card, details) => {
    setSelectedCard(card);
    setSelectedCardDetails(details);
    setIsDetailsDialogOpen(true);
  };
  
  // Close the details dialog
  const handleCloseDetailsDialog = () => {
    setIsDetailsDialogOpen(false);
  };
  
  // Preload der ersten Bilder für sichtbare Karten
  const preloadVisibleCardImages = useCallback((cardsToPreload, detailsMap) => {
    if (!cardsToPreload || cardsToPreload.length === 0) return;
    
    // Für die ersten sichtbaren Karten Bilder vorladen
    const preloadBatch = cardsToPreload.slice(0, 8); // Die ersten 8 Karten sofort laden
    
    const preloadUrls = preloadBatch.map(card => {
      const details = detailsMap.get(card.card_detail_id);
      // Verwende nur die Hauptbild-URL
      const url = getCardImageUrl(card, details, showBaseImages);
      return url ? url : null;
    }).filter(Boolean);
    
    // Füge URLs zur Preload-Queue hinzu
    if (preloadUrls.length > 0) {
      addToPreloadQueue(preloadUrls);
    }
  }, [showBaseImages]);
  
  // Daten laden beim Mount (nur einmal!)
  useEffect(() => {
    // Set initial loading state
    setIsInitialLoading(true);
    
    // Zuerst die Card Details laden
    axios.get('https://api2.splinterlands.com/cards/get_details')
      .then(res => {
        const details = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setCardDetails(details);
        
        // Erstelle eine Map für schnelleren Zugriff auf Details
        const detailsMap = new Map();
        details.forEach(detail => {
          detailsMap.set(detail.id, detail);
        });
        
        const index = {}; // Suchindex hier bereits erstellen
        
        // Add cache buster parameter to ensure fresh market data
        const cacheBuster = Math.random().toString(36).substring(2, 15);
        return axios.get(`https://api.splinterlands.com/market/for_sale_grouped?v=${cacheBuster}`)
          .then(marketRes => {
            const marketData = Array.isArray(marketRes.data) ? marketRes.data : marketRes.data?.data || [];
            
            // Kartennamen aus den Details hinzufügen, gleichzeitig den Suchindex aufbauen
            const enhancedMarketData = marketData.map(card => {
              const cardDetails = detailsMap.get(card.card_detail_id);
              
              // API liefert bereits USD-Preise, keine Konvertierung nötig
              const originalPrice = card.low_price;
              let normalizedPrice = originalPrice;
              
              if (normalizedPrice !== undefined && normalizedPrice !== null) {
                normalizedPrice = Number(normalizedPrice);
              }
              
              // Indiziere die Karte während der Verarbeitung, wenn Details verfügbar sind
              if (cardDetails) {
                // Indexierung des Namens
                if (cardDetails.name) {
                  // Einzelne Wortteile indexieren
                  const nameParts = cardDetails.name.toLowerCase().split(' ');
                  nameParts.forEach(part => {
                    if (part.length > 2) { // Ignoriere sehr kurze Wörter
                      if (!index[part]) index[part] = new Set();
                      index[part].add(card.card_detail_id);
                    }
                  });
                  
                  // Vollständiger Name
                  const fullName = cardDetails.name.toLowerCase();
                  if (!index[fullName]) index[fullName] = new Set();
                  index[fullName].add(card.card_detail_id);
                }
                 // Indexierung des Elements/der Farbe
                if (cardDetails.color) {
                  const elementInfo = getElementInfo(cardDetails.color);
                  if (elementInfo) {
                    const elementName = elementInfo.name.toLowerCase();
                    if (!index[elementName]) index[elementName] = new Set();
                    index[elementName].add(card.card_detail_id);
                  }
                }

                // Indexierung der Rarität
                if (cardDetails.rarity) {
                  const rarityInfo = getRarityInfo(cardDetails.rarity);
                  if (rarityInfo) {
                    const rarityName = rarityInfo.name.toLowerCase();
                    if (!index[rarityName]) index[rarityName] = new Set();
                    index[rarityName].add(card.card_detail_id);
                  }
                }
                
                // Indexierung des Typs
                if (cardDetails.type) {
                  const typeName = cardDetails.type.toLowerCase();
                  if (!index[typeName]) index[typeName] = new Set();
                  index[typeName].add(card.card_detail_id);
                }
                
                return {
                  ...card,
                  low_price: normalizedPrice,
                  card_name: cardDetails.name // Speichere den Namen direkt in den Kartendaten
                };
              }
              return {
                ...card,
                low_price: normalizedPrice
              };
            });
            
            // Zähle potentielle Duplikate
            const uniqueSignatures = new Set();
            enhancedMarketData.forEach(card => {
              const signature = `${card.card_detail_id}_${card.gold ? 'gold' : 'normal'}_${card.foil || 0}_${card.edition || 0}`;
              uniqueSignatures.add(signature);
            });
            
            // Konvertiere Sets zu Arrays für bessere Kompatibilität
            const finalIndex = {};
            Object.keys(index).forEach(key => {
              finalIndex[key] = Array.from(index[key]);
            });
            
            // Setze den Index direkt
            setSearchIndex(finalIndex);
            
            setCards(enhancedMarketData);

            // Starte das Vorladen der Bilder für die ersten sichtbaren Karten
            preloadVisibleCardImages(enhancedMarketData, detailsMap);
            
            // Don't set isInitialLoading to false here - wait for visibleCards to be ready
          });
      })
      .catch(err => {
        console.error("Error loading card data:", err);
        setIsInitialLoading(false); // Only stop loading on error
      });
  }, [preloadVisibleCardImages]);
  


  // Hilfsfunktion für Details
  const getCardDetails = useCallback((cardDetailId) => cardDetails.find(detail => detail.id === cardDetailId) || {}, [cardDetails]);
  
  // Neue Funktion zum Filtern der Karten - nur Splinterlands-Karten anzeigen
  const filterSplinterlandsCards = useCallback((cardsArray) => {
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      
      // Prüfen, ob die Karte aus Splinterlands oder einem anderen Spiel stammt
      // Bekannte nicht-Splinterlands Kartentypen ausfiltern
      if (details.type === "Spell" || // Spells ausfiltern
          details.type === "Rune" ||  // Runes ausfiltern
          details.type === "Hero" ||  // Heroes von anderen Spielen
          details.name?.includes("Celene") || // Celene-Karten
          details.editions?.includes("celene")) { // Durch Edition identifizieren
        return false;
      }
      
      // Nur Splinterlands Kartentypen durchlassen
      return (
        details.type === "Monster" || 
        details.type === "Summoner" || 
        details.type === "Arcana" ||
        // Wenn kein Typ angegeben ist, aber eine bekannte Edition vorliegt
        (details.edition >= 0 && details.edition <= 10)
      );
    });
  }, [getCardDetails]);
  
  // Diese Funktionen wurden in die cardImageUtils.js Datei verschoben
  
  // Die Funktionen zur Bildgenerierung wurden nach cardImageUtils.js verschoben
  // Wir nutzen nun die LazyCardImage Komponente
  
  // Helper: Get the element info (now uses constants)
  const getElementInfo = (color) => {
    return getElementInfoByColor(color);
  };
  
  // Helper: Get the rarity info (now uses constants)
  const getRarityInfo = (rarity) => {
    return getRarityInfoByValue(rarity);
  };

  // Dedupliziere Karten basierend auf relevanten Eigenschaften
  const deduplicateCards = (cardsArray) => {
    const seen = new Map();
    return cardsArray.filter(card => {
      // Erstelle einen eindeutigen Schlüssel für jede Kartenvariante
      const uniqueSignature = `${card.card_detail_id}_${card.gold ? 'gold' : 'normal'}_${card.foil || 0}_${card.edition || 0}`;
      
      // Wenn wir diesen Schlüssel schon gesehen haben, überspringe diese Karte
      if (seen.has(uniqueSignature)) {
        return false;
      }
      
      // Ansonsten markieren wir, dass wir diese Variante gesehen haben
      seen.set(uniqueSignature, true);
      return true;
    });
  };
  
  // Filtere Karten basierend auf dem Suchbegriff
  const filterCardsBySearchTerm = useCallback((cardsArray, term) => {
    // Wenn kein Suchbegriff vorhanden ist, alle Karten zurückgeben
    if (!term) return cardsArray;
    
    // Suchbegriff für Vergleich normalisieren
    const normalizedTerm = term.toLowerCase().trim();
    
    // Wenn wir einen Index haben, diesen für die Suche nutzen
    if (Object.keys(searchIndex).length > 0) {
      // Direkte Treffer im Index suchen
      if (searchIndex[normalizedTerm]) {
        const matchingIds = new Set(searchIndex[normalizedTerm]);
        return cardsArray.filter(card => matchingIds.has(card.card_detail_id));
      }
      
      // Teilweise Übereinstimmungen im Index suchen
      const matchingIds = new Set();
      Object.keys(searchIndex).forEach(key => {
        if (key.includes(normalizedTerm)) {
          searchIndex[key].forEach(id => matchingIds.add(id));
        }
      });
      
      if (matchingIds.size > 0) {
        return cardsArray.filter(card => matchingIds.has(card.card_detail_id));
      }
    }
    
    // Fallback: Manuelle Suche, wenn kein Index verfügbar oder keine Treffer im Index
    return cardsArray.filter(card => {
      // Hole die Details für die Karte
      const details = getCardDetails(card.card_detail_id);
      
      // Suche in verschiedenen Feldern
      return (
        // Nach Namen suchen
        (details.name && details.name.toLowerCase().includes(normalizedTerm)) ||
        // Nach gespeichertem Namen in der Karte selbst suchen (falls vorhanden)
        (card.card_name && card.card_name.toLowerCase().includes(normalizedTerm)) ||
        // Nach Element/Farbe suchen (z.B. "fire", "water", usw.)
        (() => {
          const elementInfo = getElementInfo(details.color);
          return elementInfo && elementInfo.name.toLowerCase().includes(normalizedTerm);
        })() ||
        // Nach Rarität suchen
        (() => {
          const rarityInfo = getRarityInfo(details.rarity);
          return rarityInfo && rarityInfo.name.toLowerCase().includes(normalizedTerm);
        })() ||
        // Nach Typ suchen
        (details.type && details.type.toLowerCase().includes(normalizedTerm))
      );
    });
  }, [searchIndex, getCardDetails]);
  
  // Filtere Karten nach Elementen
  const filterCardsByElements = useCallback((cardsArray, elementIds) => {
    // Wenn keine Element-Filter ausgewählt sind, alle Karten zurückgeben
    if (!elementIds || elementIds.length === 0) return cardsArray;
    
    // Element-Namen in Farben umwandeln für den Filter
    const elementColors = elementIds.map(id => {
      const element = CARD_ELEMENTS.find(el => el.id === id);
      return element ? element.color : null;
    }).filter(Boolean);
    
    // Nach Farben filtern
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return elementColors.includes(details.color);
    });
  }, [getCardDetails]);

    // Filtere Karten nach Raritäten
  const filterCardsByRarities = useCallback((cardsArray, rarityIds) => {
    if (!rarityIds || rarityIds.length === 0) return cardsArray;
    // Mapping von Rarity-String zu Zahl (wie in den Details)
    const rarityMap = {
      common: 1,
      rare: 2,
      epic: 3,
      legendary: 4,
    };
    const allowedRarities = rarityIds.map(id => rarityMap[id]).filter(Boolean);
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return allowedRarities.includes(details.rarity);
    });
  }, [getCardDetails]);
  
  // Filtere Karten nach Set-Kategorien (hierarchisch)
  const filterCardsBySetCategories = useCallback((cardsArray, selectedCategories) => {
    if (!selectedCategories || selectedCategories.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      // Use the new matching function that checks tier and is_promo
      return matchesAnyCategory(details, selectedCategories);
    });
  }, [getCardDetails]);
  
  // Filtere Karten nach Foil-Typen
  const filterCardsByFoilTypes = useCallback((cardsArray, foilTypeIds) => {
    if (!foilTypeIds || foilTypeIds.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      // Foil-Typ direkt aus der Karte verwenden
      return foilTypeIds.includes(card.foil);
    });
  }, []);

  // Filtere Karten nach Bloodlines (sub_type)
  const filterCardsByBloodlines = useCallback((cardsArray, bloodlineIds) => {
    if (!bloodlineIds || bloodlineIds.length === 0) return cardsArray;
    
    // Convert bloodline IDs to their actual values for comparison
    const bloodlineValues = bloodlineIds.map(id => {
      const bloodline = CARD_BLOODLINES.find(bl => bl.id === id);
      return bloodline ? bloodline.value : null;
    }).filter(Boolean);
    
    if (bloodlineValues.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return details.sub_type && bloodlineValues.includes(details.sub_type);
    });
  }, [getCardDetails]);

  // Filter cards by types (Units vs Archons)
  const filterCardsByTypes = useCallback((cardsArray, typeIds) => {
    if (!typeIds || typeIds.length === 0) return cardsArray;
    
    return cardsArray.filter(card => {
      const details = getCardDetails(card.card_detail_id);
      return typeIds.some(typeId => {
        if (typeId === 'units') {
          // Units are Monster types
          return details.type === 'Monster';
        } else if (typeId === 'archons') {
          // Archons are Summoner types
          return details.type === 'Summoner';
        }
        return false;
      });
    });
  }, [getCardDetails]);
  
  const filteredByType = useMemo(() => 
    filterSplinterlandsCards(cards),
    [cards, filterSplinterlandsCards]
  );
  
  const filteredBySearch = useMemo(() => 
    filterCardsBySearchTerm(filteredByType, searchTerm),
    [filteredByType, searchTerm, filterCardsBySearchTerm]
  );
  
  const filteredByElements = useMemo(() =>
    filterCardsByElements(filteredBySearch, selectedElementFilters),
    [filteredBySearch, selectedElementFilters, filterCardsByElements]
  );

    // Filtere Karten nach Raritäten
    const filteredByRarities = useMemo(() =>
      filterCardsByRarities(filteredByElements, selectedRarities),
      [filteredByElements, selectedRarities, filterCardsByRarities]
    );
    
    // Filtere Karten nach Set-Kategorien
    const filteredBySetCategories = useMemo(() =>
      filterCardsBySetCategories(filteredByRarities, selectedSetCategories),
      [filteredByRarities, selectedSetCategories, filterCardsBySetCategories]
    );
  
  // Filtere Karten nach Foil-Typen
  const filteredByFoilTypes = useMemo(() =>
    filterCardsByFoilTypes(filteredBySetCategories, selectedFoilTypes),
    [filteredBySetCategories, selectedFoilTypes, filterCardsByFoilTypes]
  );

  // Filtere Karten nach Bloodlines
  const filteredByBloodlines = useMemo(() =>
    filterCardsByBloodlines(filteredByFoilTypes, selectedBloodlines),
    [filteredByFoilTypes, selectedBloodlines, filterCardsByBloodlines]
  );

  // Filter cards by types (Units vs Archons)
  const filteredByTypes = useMemo(() =>
    filterCardsByTypes(filteredByBloodlines, selectedTypes),
    [filteredByBloodlines, selectedTypes, filterCardsByTypes]
  );

  // Sortiere gefilterte Karten nach Preis
  const sortedCards = useMemo(() => {
    const cardsToSort = [...filteredByTypes];
    
    if (!sortOption) {
      return cardsToSort; // Keine Sortierung
    }

    return cardsToSort.sort((a, b) => {
      let priceA, priceB;

      switch (sortOption) {
        case 'price_asc':
          priceA = a.low_price || Infinity;
          priceB = b.low_price || Infinity;
          return priceA - priceB;
        
        case 'price_desc':
          priceA = a.low_price || 0;
          priceB = b.low_price || 0;
          return priceB - priceA;
        
        case 'price_bcx_asc':
          priceA = a.low_price_bcx || Infinity;
          priceB = b.low_price_bcx || Infinity;
          return priceA - priceB;
        
        case 'price_bcx_desc':
          priceA = a.low_price_bcx || 0;
          priceB = b.low_price_bcx || 0;
          return priceB - priceA;
        
        default:
          return 0;
      }
    });
  }, [filteredByTypes, sortOption]);

  const filteredCards = sortedCards;
  
  // Karten für die aktuelle Seite mit Paginierung und Deduplizierung
  const visibleCards = deduplicateCards(filteredCards.slice(0, visibleCount));
  
  // Determine if we should show loading: true if initial load OR if we have raw data but no visible cards yet
  const shouldShowLoading = isInitialLoading || (cards.length > 0 && visibleCards.length === 0 && cardDetails.length > 0);
  
  // Turn off initial loading once we have cards loaded
  useEffect(() => {
    if (isInitialLoading && cards.length > 0 && cardDetails.length > 0) {
      setIsInitialLoading(false);
    }
  }, [isInitialLoading, cards.length, cardDetails.length]);
  
  // Funktion zum Laden weiterer Karten
  const loadMoreCards = () => {
    setVisibleCount(prevCount => prevCount + cardsPerPage);
    setPage(prevPage => prevPage + 1);
  };

  // Infinite Scroll: Beobachte das Sentinel-Element
  useEffect(() => {
    if (!sentinelRef.current) return;
    if (visibleCount >= filteredCards.length) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreCards();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [visibleCount, filteredCards.length]);

  // Sort Controls Component to be passed to MarketHeader
  const sortControls = (
    <Box display="flex" alignItems="center" gap={1.5}>
      <SortIcon sx={{ color: colors.greenAccent[400], fontSize: 18, flexShrink: 0 }} />
      <FormControl size="small" sx={{ minWidth: 160, width: 200 }}>
        <InputLabel sx={{ color: colors.grey[100], fontSize: '0.8rem' }}>Sort</InputLabel>
        <Select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          label="Sort"
          MenuProps={{
            disableScrollLock: true,
            PaperProps: { style: { maxHeight: 400 } },
          }}
          sx={{
            color: colors.grey[100],
            fontSize: '0.8rem',
            '.MuiOutlinedInput-input': { py: 0.75 },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary[300],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.greenAccent[400],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.greenAccent[400],
            },
            '.MuiSvgIcon-root': {
              color: colors.grey[100],
            }
          }}
        >
          <MenuItem value="">No Sorting</MenuItem>
          <MenuItem value="price_asc">Price ↑</MenuItem>
          <MenuItem value="price_desc">Price ↓</MenuItem>
          <MenuItem value="price_bcx_asc">Price/BCX ↑</MenuItem>
          <MenuItem value="price_bcx_desc">Price/BCX ↓</MenuItem>
          <MenuItem value="price_change_desc">Highest Gains</MenuItem>
          <MenuItem value="price_change_asc">Biggest Drops</MenuItem>
          <MenuItem value="daily_sales_desc">Most Sold</MenuItem>
          <MenuItem value="daily_sales_asc">Least Sold</MenuItem>
        </Select>
      </FormControl>
      <Box 
        sx={{ 
          display: { xs: 'none', lg: 'flex' },
          backgroundColor: colors.greenAccent[600] + '15',
          border: `1px solid ${colors.greenAccent[600]}50`,
          borderRadius: 1,
          px: 1.25,
          py: 0.4,
        }}
      >
        <Typography 
          variant="body2" 
          color={colors.greenAccent[400]} 
          fontWeight="600"
          sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
        >
          {filteredCards.length}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box m="20px">
      {/* Market Header with Integrated Tabs and Sort Controls */}
      <MarketHeader 
        onInfoClick={() => setShowInfo(true)}
        onLeaderboardClick={() => setShowLeaderboard(true)}
        additionalControls={sortControls}
      />
      
      {/* Dialogs (managed by this component) */}
      <InfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      <LeaderboardDialog open={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

      {/* Initial Loading State */}
      {shouldShowLoading ? (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="400px"
        >
          <CircularProgress 
            size={60} 
            sx={{ color: colors.greenAccent[500] }} 
          />
        </Box>
      ) : (
        <>
          {/* Cards Grid Content */}
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(auto-fill, minmax(140px, 1fr))',
                sm: 'repeat(auto-fill, minmax(180px, 1fr))',
                md: 'repeat(auto-fill, minmax(200px, 1fr))',
                lg: 'repeat(auto-fill, minmax(220px, 1fr))',
              },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {visibleCards.map((card, index) => {
          const details = getCardDetails(card.card_detail_id);
          const price = card.low_price;
          
          const uniqueKey = `card_${card.card_detail_id}_${card.gold ? 'gold' : 'normal'}_${card.foil || 0}_${card.edition || 0}_${index}`;
          return (
            <Box key={uniqueKey}>
              <CardItem 
                card={card}
                details={details}
                price={price}
                showBaseImages={showBaseImages}
                refreshImageKey={refreshImageKey}
                onCardClick={handleCardClick}
              />
            </Box>
          );
        })}
          </Box>
          {/* Infinite Scroll Sentinel */}
          {visibleCount < filteredCards.length && (
            <div ref={sentinelRef} style={{ height: 1 }} />
          )}
        </>
      )}

      {/* Details Dialog */}
      {selectedCard && (
        <CardDetailsDialog
          open={isDetailsDialogOpen}
          onClose={handleCloseDetailsDialog}
          card={selectedCard}
          details={selectedCardDetails}
          showBaseImages={showBaseImages}
          refreshImageKey={refreshImageKey}
        />
      )}
      
      {/* Mobile Filter Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="filter"
          onClick={() => setIsSidebar(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            zIndex: 1000,
            backgroundColor: colors.blueAccent[600],
            '&:hover': {
              backgroundColor: colors.blueAccent[700]
            }
          }}
        >
          <FilterListIcon />
        </Fab>
      )}
    </Box>
  );
};

export default Market;